import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { SCHOOLS_REGISTRY } from '@cougargrades/models'

// ─── Mobile drawer ────────────────────────────────────────────────────────────

function MobileDrawer({
  open,
  onClose,
  activePath,
  activeSchool,
}: {
  open: boolean
  onClose: () => void
  activePath: string
  activeSchool: (typeof SCHOOLS_REGISTRY)[number] | undefined
}) {
  if (!open) return null
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Backdrop */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        style={{
          position: 'relative',
          background: 'var(--school-primary)',
          padding: '20px 24px 32px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          zIndex: 201,
        }}
      >
        {/* Top row */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="no-underline flex items-center gap-2" onClick={onClose}>
            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
              Unilytics
            </span>
            <sup style={{ fontSize: 9, fontWeight: 700, color: 'var(--school-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase', marginLeft: 2 }}>
              BETA
            </sup>
          </Link>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 22, cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col gap-1 mb-8">
          {([
            { href: '/courses', label: 'Courses' },
            { href: '/about', label: 'About' },
            { href: '/faq', label: 'FAQ' },
          ] as const).map(({ href, label }) => (
            <a
              key={href}
              href={href}
              onClick={onClose}
              style={{
                display: 'block',
                padding: '12px 0',
                fontSize: 18,
                fontWeight: 600,
                color: activePath === href ? 'var(--school-secondary)' : '#fff',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                letterSpacing: '0.01em',
              }}
            >
              {label}
            </a>
          ))}
          {/* Compare Schools — coming soon */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 0',
              opacity: 0.4,
              cursor: 'not-allowed',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>Compare Schools ⬡</span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'var(--school-secondary)', color: '#0D1B2A', padding: '2px 6px', borderRadius: 20 }}>
              SOON
            </span>
          </div>
        </nav>

        {/* School info */}
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          Current School
        </div>
        <div className="flex items-center gap-3">
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--school-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              color: '#0D1B2A',
              flexShrink: 0,
            }}
          >
            {activeSchool?.shortName ?? 'UH'}
          </div>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
            {activeSchool?.name ?? 'University of Houston'}
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Main header ──────────────────────────────────────────────────────────────

export function Header() {
  const [schoolMenuOpen, setSchoolMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const activeSchool = SCHOOLS_REGISTRY.find(s => s.isActive)
  const { location } = useRouterState()
  const activePath = location.pathname

  return (
    <>
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        activePath={activePath}
        activeSchool={activeSchool}
      />

      <header
        style={{
          background: 'var(--school-primary)',
          color: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 1px 0 rgba(255,255,255,0.08)',
        }}
      >
        <div className="new-container">
          <div className="flex items-center justify-between gap-4" style={{ height: 56 }}>

            {/* ── Wordmark ── */}
            <Link to="/" className="no-underline flex items-center gap-1.5 flex-shrink-0">
              <span
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: '-0.04em',
                  fontFamily: 'var(--font-family-sans)',
                  lineHeight: 1,
                }}
              >
                Unilytics
              </span>
              <sup
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: 'var(--school-secondary)',
                  textTransform: 'uppercase',
                  verticalAlign: 'super',
                  lineHeight: 1,
                }}
              >
                BETA
              </sup>
            </Link>

            {/* ── Desktop nav ── */}
            <nav className="hidden lg:flex items-center gap-4">
              <DesktopNavLink href="/courses" active={activePath === '/courses'}>
                Courses
              </DesktopNavLink>
              {/* Compare Schools — coming soon */}
              <span
                className="relative inline-flex items-center gap-1"
                style={{
                  padding: '6px 12px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.35)',
                  cursor: 'not-allowed',
                  letterSpacing: '0.02em',
                  userSelect: 'none',
                  lineHeight: '28px',
                  whiteSpace: 'nowrap',
                }}
              >
                Compare Schools ⬡
                <span
                  className="text-[8px] font-bold uppercase px-1 py-0.5 rounded-full"
                  style={{
                    background: 'var(--school-secondary)',
                    color: '#0D1B2A',
                    opacity: 0.85,
                    lineHeight: 1,
                    verticalAlign: 'super',
                    whiteSpace: 'nowrap',
                  }}
                >
                  SOON
                </span>
              </span>
              <DesktopNavLink href="/about" active={activePath === '/about'}>
                About
              </DesktopNavLink>
              <DesktopNavLink href="/faq" active={activePath.startsWith('/faq')}>
                FAQ
              </DesktopNavLink>
            </nav>

            {/* ── Right side ── */}
            <div className="flex items-center gap-3">

              {/* School selector — desktop */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setSchoolMenuOpen(v => !v)}
                  className="flex items-center gap-2"
                  style={{
                    padding: '6px 14px 6px 8px',
                    borderRadius: 99,
                    border: '1px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.18)' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)' }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'var(--school-secondary)',
                      color: '#0D1B2A',
                      fontSize: 9,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {activeSchool?.shortName ?? 'UH'}
                  </span>
                  <span>{activeSchool?.name ?? 'University of Houston'}</span>
                  <span style={{ opacity: 0.5, fontSize: 10 }}>▾</span>
                </button>

                {schoolMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 py-1 z-50"
                    style={{
                      top: '100%',
                      background: '#fff',
                      borderRadius: 14,
                      border: '1px solid rgba(13,27,42,0.08)',
                      boxShadow: '0 12px 40px rgba(13,27,42,0.18)',
                      minWidth: 240,
                    }}
                    onMouseLeave={() => setSchoolMenuOpen(false)}
                  >
                    <div style={{ padding: '8px 14px 6px', borderBottom: '1px solid rgba(13,27,42,0.06)', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Select School
                      </span>
                    </div>
                    {SCHOOLS_REGISTRY.map(school => (
                      <div
                        key={school.id}
                        className="flex items-center justify-between"
                        style={{ padding: '8px 14px', opacity: school.isActive ? 1 : 0.5, cursor: school.isActive ? 'pointer' : 'not-allowed' }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: school.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontSize: 13, color: '#0D1B2A', fontWeight: school.isActive ? 600 : 400 }}>
                            {school.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {school.isActive && (
                            <span style={{ fontSize: 13, color: '#059669' }}>✓</span>
                          )}
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 7px',
                              borderRadius: 6,
                              color: school.isActive ? '#059669' : '#94A3B8',
                              background: school.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)',
                            }}
                          >
                            {school.isActive ? 'Live' : 'Soon'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                className="lg:hidden"
                onClick={() => setMobileOpen(true)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 18,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  lineHeight: 1.2,
                }}
              >
                ☰
              </button>
            </div>

          </div>
        </div>
      </header>
    </>
  )
}

// ─── Desktop nav link ─────────────────────────────────────────────────────────

function DesktopNavLink({
  href,
  active,
  children,
}: {
  href: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      className="no-underline transition-colors"
      style={{
        padding: '6px 12px',
        fontSize: 14,
        fontWeight: 500,
        color: active ? 'var(--school-secondary)' : 'rgba(255,255,255,0.75)',
        borderBottom: active ? '2px solid var(--school-secondary)' : '2px solid transparent',
        letterSpacing: '0.02em',
        display: 'inline-block',
        lineHeight: '28px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => {
        if (!active) (e.currentTarget as HTMLAnchorElement).style.color = '#fff'
      }}
      onMouseLeave={e => {
        if (!active) (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.75)'
      }}
    >
      {children}
    </a>
  )
}
